import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, FileText, Folder, ScrollText } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import PolicyViewer from '../common/PolicyViewer';

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const Policies = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFolder = searchParams.get('folder');

  const [categories, setCategories] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingPolicy, setViewingPolicy] = useState(null);

  useEffect(() => {
    api
      .get('/policies/categories')
      .then((response) => setCategories(response.data.categories || []))
      .catch(() => toast.error('Failed to fetch policy folders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeFolder) {
      setPolicies([]);
      return;
    }
    setLoading(true);
    api
      .get(`/policies?category=${encodeURIComponent(activeFolder)}`)
      .then((response) => setPolicies(response.data.policies || []))
      .catch(() => toast.error('Failed to fetch policies'))
      .finally(() => setLoading(false));
  }, [activeFolder]);

  const activeCategory = categories.find((category) => category.value === activeFolder);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Company Policies</h1>
      </div>

      <nav className="flex items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className={activeFolder ? 'text-primary-600 hover:underline' : 'font-semibold text-gray-900'}
        >
          All Folders
        </button>
        {activeCategory && (
          <>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="font-semibold text-gray-900">{activeCategory.label}</span>
          </>
        )}
      </nav>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <LoadingSpinner size="lg" className="mx-auto" />
        </div>
      ) : !activeFolder ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {categories.map((category) => (
            <button
              key={category.value}
              type="button"
              onClick={() => setSearchParams({ folder: category.value })}
              className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                <Folder className="h-6 w-6" />
              </span>
              <span className="min-w-0">
                <span className="block truncate font-semibold text-gray-900">{category.label}</span>
                <span className="text-sm text-gray-500">
                  {category.count} {category.count === 1 ? 'policy' : 'policies'}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : policies.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
          {policies.map((policy) => (
            <button
              key={policy._id}
              type="button"
              onClick={() => setViewingPolicy(policy)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50"
            >
              <FileText className="h-5 w-5 shrink-0 text-red-500" />
              <span className="min-w-0 flex-1 truncate font-medium text-gray-900">{policy.title}</span>
              <span className="hidden text-sm text-gray-500 sm:block">Updated {formatDate(policy.updatedAt)}</span>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <ScrollText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No policies yet</h3>
          <p className="mt-1 text-sm text-gray-500">No policies have been added to this folder.</p>
        </div>
      )}

      {viewingPolicy && <PolicyViewer policy={viewingPolicy} onClose={() => setViewingPolicy(null)} />}
    </div>
  );
};

export default Policies;
